// ============================================================================
// AI ORIGINAL GENERATION SNAPSHOTS
// ============================================================================
// Persists the first AI-generated Stage 4 scope and Stage 5 quote draft into
// project.aiContext.originalGeneratedSnapshots. The first captured snapshot is
// intentionally immutable: later user edits or regenerations must not rewrite
// the baseline that training and QM Assistant use for comparison.

export const ORIGINAL_SNAPSHOT_SCHEMA_VERSION = 1;

const STAGE4_SCOPE_ID = 'original-stage-4-scope';
const STAGE5_QUOTE_ID = 'original-stage-5-quote';

function isPlainObject(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function safeClone(value) {
    if (value === undefined || value === null) return null;
    try {
        return JSON.parse(JSON.stringify(value));
    } catch {
        return value;
    }
}

const DURABLE_PHOTO_LOCATOR_KEYS = new Set([
    'fileMetadataId', 'file_metadata_id', 'fileId', 'file_id',
    'sourceFileMetadataId', 'source_file_metadata_id',
    's3Key', 's3_key', 'fileS3Key', 'file_s3_key',
    'cloudObjectKey', 'cloud_object_key', 'pdfS3Key', 'pdf_s3_key',
    'sourcePdfS3Key', 'source_pdf_s3_key',
]);
const SCOPE_PHOTO_ARRAY_KEYS = [
    'images', 'photos',
    'assignedPhotos', 'assigned_photos',
    'selectedPhotos', 'selected_photos',
];
const SCOPE_ARRAY_KEYS = ['scope', 'scopeItems', 'scope_items'];
const HERO_IMAGE_KEYS = ['heroImage', 'hero_image', 'selectedHeroImage', 'selected_hero_image'];

function stripPhotoLocatorFields(photo) {
    if (!isPlainObject(photo)) return photo;
    let changed = false;
    const projected = { ...photo };
    for (const key of DURABLE_PHOTO_LOCATOR_KEYS) {
        if (!Object.prototype.hasOwnProperty.call(projected, key)) continue;
        delete projected[key];
        changed = true;
    }
    return changed ? projected : photo;
}

function stripScopeItemPhotoLocators(item) {
    if (!isPlainObject(item)) return item;
    let projected = item;
    for (const key of SCOPE_PHOTO_ARRAY_KEYS) {
        if (!Array.isArray(projected[key])) continue;
        const photos = projected[key].map(stripPhotoLocatorFields);
        if (photos.some((photo, index) => photo !== projected[key][index])) {
            if (projected === item) projected = { ...item };
            projected[key] = photos;
        }
    }
    return projected;
}

/**
 * Original AI scope remains useful audit/training evidence, but it must not
 * continue to own customer object-store bytes after the live quote releases
 * them. Preserve photo identity, labels and captions while projecting away
 * only explicit durable ownership locators from known scope photo lanes.
 */
export function stripOriginalScopePhotoLocators(scopeOrEnvelope) {
    if (Array.isArray(scopeOrEnvelope)) {
        return scopeOrEnvelope.map(stripScopeItemPhotoLocators);
    }
    if (!isPlainObject(scopeOrEnvelope)) return scopeOrEnvelope;
    let projected = scopeOrEnvelope;
    for (const key of SCOPE_ARRAY_KEYS) {
        if (!Array.isArray(projected[key])) continue;
        const scope = projected[key].map(stripScopeItemPhotoLocators);
        if (scope.some((item, index) => item !== projected[key][index])) {
            if (projected === scopeOrEnvelope) projected = { ...scopeOrEnvelope };
            projected[key] = scope;
        }
    }
    return projected;
}

function stripQuoteSnapshotPhotoLocators(payload) {
    if (!isPlainObject(payload)) return stripOriginalScopePhotoLocators(payload);
    let projected = stripOriginalScopePhotoLocators(payload);
    for (const headerKey of ['header', 'quoteHeader', 'quote_header']) {
        const header = projected[headerKey];
        if (!isPlainObject(header)) continue;
        let projectedHeader = header;
        for (const heroKey of HERO_IMAGE_KEYS) {
            const hero = stripPhotoLocatorFields(projectedHeader[heroKey]);
            if (hero === projectedHeader[heroKey]) continue;
            if (projectedHeader === header) projectedHeader = { ...header };
            projectedHeader[heroKey] = hero;
        }
        if (projectedHeader !== header) {
            if (projected === payload) projected = { ...payload };
            projected[headerKey] = projectedHeader;
        }
    }
    for (const heroKey of HERO_IMAGE_KEYS) {
        const hero = stripPhotoLocatorFields(projected[heroKey]);
        if (hero === projected[heroKey]) continue;
        if (projected === payload) projected = { ...payload };
        projected[heroKey] = hero;
    }
    return projected;
}

function canonicalOriginalSnapshot(snapshot) {
    if (!isPlainObject(snapshot)) return snapshot;
    const payload = snapshot.kind === 'quote' || Number(snapshot.stage) === 5
        ? stripQuoteSnapshotPhotoLocators(snapshot.payload)
        : stripOriginalScopePhotoLocators(snapshot.payload);
    return payload === snapshot.payload ? snapshot : { ...snapshot, payload };
}

function moneyNum(value, fallback = 0) {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    const parsed = Number(String(value ?? '').replace(/,/g, '').trim());
    return Number.isFinite(parsed) ? parsed : fallback;
}

function firstPresent(...values) {
    for (const value of values) {
        if (value === undefined || value === null) continue;
        if (typeof value === 'string' && value.trim() === '') continue;
        return value;
    }
    return null;
}

function getScopeFromSnapshotPayload(snapshot = {}) {
    const payload = snapshot?.payload;
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.scope)) return payload.scope;
    return [];
}

function summariseScopeItems(scope = []) {
    const items = Array.isArray(scope) ? scope : [];
    const totalAmount = items.reduce((sum, item) => sum + moneyNum(item?.amount), 0);
    return {
        itemCount: items.length,
        totalAmount,
        titles: items.slice(0, 12).map(item => item?.title || item?.name || 'Untitled'),
    };
}

function summariseQuotePayload(quoteData = {}) {
    const scope = Array.isArray(quoteData?.scope) ? quoteData.scope : [];
    return {
        ...summariseScopeItems(scope),
        scopeObjective: firstPresent(
            quoteData.scopeObjective,
            quoteData.objective,
            quoteData.summary?.scopeObjective,
            quoteData.meta?.scopeObjective,
        ),
        quoteNumber: firstPresent(
            quoteData.meta?.quoteNumber,
            quoteData.quoteNumber,
            quoteData.number,
        ),
        title: firstPresent(
            quoteData.header?.title,
            quoteData.title,
        ),
        preparedFor: firstPresent(
            quoteData.header?.preparedFor,
            quoteData.preparedFor,
        ),
        isGstInclusive: quoteData.isGstInclusive !== undefined ? !!quoteData.isGstInclusive : null,
    };
}

export function createOriginalGeneratedSnapshot({
    id,
    projectId = null,
    stage,
    kind,
    source = 'unknown',
    payload,
    model = null,
    generatedAt = null,
    capturedAt = null,
    metadata = {},
} = {}) {
    const rawClonedPayload = safeClone(payload);
    const clonedPayload = kind === 'quote' || Number(stage) === 5
        ? stripQuoteSnapshotPhotoLocators(rawClonedPayload)
        : stripOriginalScopePhotoLocators(rawClonedPayload);
    if (clonedPayload === null || clonedPayload === undefined) return null;

    const summary = kind === 'quote'
        ? summariseQuotePayload(clonedPayload)
        : summariseScopeItems(Array.isArray(clonedPayload) ? clonedPayload : clonedPayload?.scope);

    return {
        id: id || `original-stage-${stage}-${kind}`,
        schemaVersion: ORIGINAL_SNAPSHOT_SCHEMA_VERSION,
        projectId: projectId || null,
        stage,
        kind,
        source,
        generatedAt: generatedAt || new Date().toISOString(),
        capturedAt: capturedAt || new Date().toISOString(),
        model: model || null,
        summary,
        metadata: {
            immutable: true,
            purpose: 'training-baseline',
            ...metadata,
        },
        payload: clonedPayload,
    };
}

export function getOriginalGeneratedSnapshots(aiContextOrProject = {}) {
    const aiContext = aiContextOrProject?.aiContext || aiContextOrProject?.ai_context || aiContextOrProject || {};
    const raw = aiContext?.originalGeneratedSnapshots
        || aiContext?.original_generated_snapshots
        || [];
    if (Array.isArray(raw)) return raw.filter(isPlainObject);
    if (isPlainObject(raw)) return Object.values(raw).filter(isPlainObject);
    return [];
}

/**
 * Select the narrow immutable-baseline subset that may cross a cold reload.
 * Project-list summaries intentionally expose only lifecycle aiContext, so the
 * full loaded Project keeps this bounded copy in an App ref for the next save.
 * Arbitrary diagnostic, learning, pricing and assistant context is excluded.
 */
export function getOriginalSnapshotPersistenceContext(aiContextOrProject = {}) {
    const project = isPlainObject(aiContextOrProject) ? aiContextOrProject : {};
    const aiContext = project?.aiContext || project?.ai_context || project || {};
    const snapshots = getOriginalGeneratedSnapshots(aiContext)
        .map(snapshot => canonicalOriginalSnapshot(safeClone(snapshot)));
    const rawOriginalScope = project?.originalAiScope
        ?? project?.original_ai_scope
        ?? aiContext?.originalScope
        ?? aiContext?.original_scope
        ?? null;
    const originalScope = stripOriginalScopePhotoLocators(safeClone(rawOriginalScope));
    const stage4 = snapshots.find(snapshot => Number(snapshot?.stage) === 4 && snapshot?.kind === 'scope');
    const stage5 = snapshots.find(snapshot => Number(snapshot?.stage) === 5 && snapshot?.kind === 'quote');
    const context = {};

    if (snapshots.length > 0) {
        context.originalGeneratedSnapshots = snapshots;
        context.originalGeneratedSnapshotSchemaVersion = Number(
            aiContext?.originalGeneratedSnapshotSchemaVersion
            ?? aiContext?.original_generated_snapshot_schema_version
            ?? ORIGINAL_SNAPSHOT_SCHEMA_VERSION,
        ) || ORIGINAL_SNAPSHOT_SCHEMA_VERSION;
        context.originalStage4ScopeSnapshotId = stage4?.id
            || aiContext?.originalStage4ScopeSnapshotId
            || aiContext?.original_stage_4_scope_snapshot_id
            || null;
        context.originalStage5QuoteSnapshotId = stage5?.id
            || aiContext?.originalStage5QuoteSnapshotId
            || aiContext?.original_stage_5_quote_snapshot_id
            || null;
    }
    if (originalScope !== null && originalScope !== undefined) {
        context.originalScope = originalScope;
    }
    return context;
}

function hasSnapshot(snapshots, { stage, kind }) {
    return snapshots.some(snapshot => Number(snapshot?.stage) === Number(stage) && snapshot?.kind === kind);
}

function withSnapshotPointers(aiContext, snapshots) {
    const stage4 = snapshots.find(snapshot => Number(snapshot?.stage) === 4 && snapshot?.kind === 'scope');
    const stage5 = snapshots.find(snapshot => Number(snapshot?.stage) === 5 && snapshot?.kind === 'quote');
    const {
        originalGeneratedSnapshots: _currentSnapshots,
        original_generated_snapshots: _legacySnapshots,
        ...canonicalContext
    } = aiContext;
    return {
        ...canonicalContext,
        originalGeneratedSnapshots: snapshots,
        originalGeneratedSnapshotSchemaVersion: ORIGINAL_SNAPSHOT_SCHEMA_VERSION,
        originalStage4ScopeSnapshotId: stage4?.id || aiContext?.originalStage4ScopeSnapshotId || null,
        originalStage5QuoteSnapshotId: stage5?.id || aiContext?.originalStage5QuoteSnapshotId || null,
    };
}

export function ensureOriginalGeneratedSnapshots(aiContext = {}, {
    projectId = null,
    originalAiScope = null,
    quoteData = null,
    canCaptureQuoteData = false,
    model = null,
    capturedAt = null,
    metadata = {},
} = {}) {
    const baseContext = isPlainObject(aiContext) ? { ...aiContext } : {};
    const snapshots = getOriginalGeneratedSnapshots(baseContext)
        .map(snapshot => canonicalOriginalSnapshot({ ...snapshot }));
    const added = [];
    const now = capturedAt || new Date().toISOString();

    if (Array.isArray(originalAiScope) && originalAiScope.length > 0 && !hasSnapshot(snapshots, { stage: 4, kind: 'scope' })) {
        const snapshot = createOriginalGeneratedSnapshot({
            id: STAGE4_SCOPE_ID,
            projectId,
            stage: 4,
            kind: 'scope',
            source: 'stage-4-ai-scope',
            payload: originalAiScope,
            model,
            generatedAt: now,
            capturedAt: now,
            metadata: {
                captureQuality: 'original-ai-scope',
                ...metadata,
            },
        });
        if (snapshot) {
            snapshots.push(snapshot);
            added.push(snapshot);
        }
    }

    if (canCaptureQuoteData && isPlainObject(quoteData) && !hasSnapshot(snapshots, { stage: 5, kind: 'quote' })) {
        const quoteGeneratedAt = firstPresent(
            quoteData.meta?.generatedAt,
            quoteData.generatedAt,
            quoteData.generated_at,
            now,
        );
        const snapshot = createOriginalGeneratedSnapshot({
            id: STAGE5_QUOTE_ID,
            projectId,
            stage: 5,
            kind: 'quote',
            source: 'stage-5-quote-generation',
            payload: quoteData,
            model,
            generatedAt: quoteGeneratedAt,
            capturedAt: now,
            metadata: {
                captureQuality: 'first-generated-quote',
                ...metadata,
            },
        });
        if (snapshot) {
            snapshots.push(snapshot);
            added.push(snapshot);
        }
    }

    return {
        aiContext: withSnapshotPointers(baseContext, snapshots),
        added,
    };
}

export function formatOriginalGeneratedSnapshotsForAssistant(snapshots = [], {
    stage = 'all',
    includePayload = false,
    payloadCharLimit = 24000,
} = {}) {
    const wantedStage = String(stage || 'all').toLowerCase();
    const filtered = getOriginalGeneratedSnapshots({ originalGeneratedSnapshots: snapshots })
        .filter(snapshot => {
            if (wantedStage === 'all') return true;
            if (wantedStage === 'stage-4' || wantedStage === '4') return Number(snapshot.stage) === 4;
            if (wantedStage === 'stage-5' || wantedStage === '5') return Number(snapshot.stage) === 5;
            return true;
        });

    if (filtered.length === 0) {
        return 'No original AI generation snapshot is saved for this project yet.';
    }

    const compact = filtered.map(snapshot => ({
        id: snapshot.id,
        stage: snapshot.stage,
        kind: snapshot.kind,
        source: snapshot.source,
        generatedAt: snapshot.generatedAt,
        capturedAt: snapshot.capturedAt,
        model: snapshot.model || null,
        summary: snapshot.summary || {},
        ...(includePayload ? { payload: snapshot.payload } : {}),
    }));

    let json = JSON.stringify({
        note: 'These are immutable first-generation baselines. Use them to compare QuoteMaker original output against user-edited current scope/quote.',
        snapshots: compact,
    }, null, 2);

    if (includePayload && json.length > payloadCharLimit) {
        json = json.slice(0, payloadCharLimit)
            + `\n...truncated at ${payloadCharLimit} characters; rerun with a narrower stage filter for the exact payload.`;
    }

    return json;
}

export function getSnapshotScopeItems(snapshot = {}) {
    return getScopeFromSnapshotPayload(snapshot);
}
